import React, { useState } from 'react';
import { useWorldStore } from '../state/worldStore';

// NpcPlacePanel — on-screen controls for the ?place=edit resident-placement
// editor. Select a resident (here or by clicking them in-scene), nudge with the
// pad (Up/Down = depth), rotate, then copy the post line into roster.json. Moves
// dispatch the same arrow/[ ] keys the selected Npc listens for (no coupling).
const safe = (side, base) => `max(${base}px, env(safe-area-inset-${side}))`;
const key = (k, shift = false) => window.dispatchEvent(new KeyboardEvent('keydown', { key: k, shiftKey: shift, bubbles: true }));
const savedPos = (npc) => ({ pos: npc.post, rotY: npc.rotationY || 0, height: npc.height || 1.8 });

const panel = {
  position: 'fixed', right: safe('right', 16), top: `calc(${safe('top', 16)} + 200px)`, zIndex: 40,
  width: 200, pointerEvents: 'auto', color: '#ffe9c4', font: '12.5px system-ui, sans-serif',
  background: 'rgba(16,10,6,0.82)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 12,
  padding: '12px 12px 14px', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
};
const btn = {
  pointerEvents: 'auto', background: 'rgba(255,178,94,0.14)', color: '#ffd27d',
  border: '1px solid rgba(255,178,94,0.4)', borderRadius: 8, padding: '7px 0', cursor: 'pointer',
  font: '600 13px system-ui', textAlign: 'center', userSelect: 'none',
};
const arrow = { ...btn, padding: '9px 0' };

export default function NpcPlacePanel() {
  const npcList = useWorldStore((s) => s.npcList);
  const selectedSlug = useWorldStore((s) => s.npcEdit.slug);
  const setNpcEdit = useWorldStore((s) => s.setNpcEdit);
  const ON = useWorldStore((s) => s.adminTool) === 'place';
  const [fine, setFine] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!ON) return null;

  const sel = npcList.find((n) => n.slug === selectedSlug) || null;

  const copyLine = () => {
    if (!sel) return;
    const { pos, rotY, height } = savedPos(sel);
    const line = `"post": [${pos.join(', ')}], "rotationY": ${rotY}, "height": ${height}`;
    navigator.clipboard?.writeText(line).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1200); }).catch(() => {});
  };

  return (
    <div style={panel}>
      <div style={{ fontSize: 10.5, letterSpacing: 1.3, textTransform: 'uppercase', opacity: 0.7, marginBottom: 8 }}>Place residents</div>

      {/* Resident picker */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10, maxHeight: 148, overflowY: 'auto' }}>
        {npcList.length === 0 && <div style={{ opacity: 0.6, fontSize: 11.5 }}>loading roster…</div>}
        {npcList.map((n) => (
          <button key={n.slug} onClick={() => setNpcEdit({ slug: n.slug })}
            style={{ ...btn, fontSize: 11.5, padding: '6px 8px', textAlign: 'left',
              background: n.slug === selectedSlug ? '#ffb25e' : 'rgba(255,178,94,0.14)',
              color: n.slug === selectedSlug ? '#1c1206' : '#ffd27d' }}>
            {n.name || n.slug}
          </button>
        ))}
      </div>

      {!sel ? (
        <div style={{ opacity: 0.6, fontSize: 11.5 }}>Pick a resident (or click one in the scene) to move it.</div>
      ) : (
        <>
          {/* Nudge pad — Up/Down = depth (z), Left/Right = across (x) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5, marginBottom: 4 }}>
            <span />
            <button style={arrow} onClick={() => key('ArrowUp', fine)}>↑</button>
            <span />
            <button style={arrow} onClick={() => key('ArrowLeft', fine)}>←</button>
            <div style={{ display: 'grid', placeItems: 'center', fontSize: 10, opacity: 0.6 }}>move</div>
            <button style={arrow} onClick={() => key('ArrowRight', fine)}>→</button>
            <span />
            <button style={arrow} onClick={() => key('ArrowDown', fine)}>↓</button>
            <span />
          </div>

          <div style={{ display: 'flex', gap: 5, margin: '8px 0 5px' }}>
            <button style={{ ...btn, flex: 1 }} onClick={() => key('[')}>⟲ rotate</button>
            <button style={{ ...btn, flex: 1 }} onClick={() => key(']')}>rotate ⟳</button>
          </div>
          <div style={{ display: 'flex', gap: 5, margin: '0 0 5px' }}>
            <button style={{ ...btn, flex: 1 }} onClick={() => key('-', fine)}>− size</button>
            <button style={{ ...btn, flex: 1 }} onClick={() => key('=', fine)}>size +</button>
          </div>
          <div style={{ display: 'flex', gap: 5, margin: '0 0 8px' }}>
            <button style={{ ...btn, flex: 1 }} onClick={() => key('j', fine)}>▼ lower</button>
            <button style={{ ...btn, flex: 1 }} onClick={() => key('u', fine)}>raise ▲</button>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '4px 0 10px', cursor: 'pointer', opacity: 0.9 }}>
            <input type="checkbox" checked={fine} onChange={(e) => setFine(e.target.checked)} />
            fine steps (0.2)
          </label>

          <button style={{ ...btn, width: '100%', background: copied ? '#9dff7d' : 'rgba(255,178,94,0.14)', color: copied ? '#0c2c06' : '#ffd27d' }} onClick={copyLine}>
            {copied ? '✓ copied' : 'copy post line'}
          </button>
          <div style={{ opacity: 0.55, fontSize: 10.5, marginTop: 8, lineHeight: 1.35 }}>
            Paste the post/rotationY into this resident's roster.json entry. Up/Down = depth.
          </div>
        </>
      )}
    </div>
  );
}
